import React from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { WeDoDevLogo } from '../components/Logo';
import { ContactButton } from '../components';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const Page404Container = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ErrorSection = styled.section`
  position: relative;
  background-color: #011D3A;
  color: white;
  padding: 80px 20px 120px;
  text-align: center;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:before {
    content: '';
    position: absolute;
    top: -20%;
    right: -20%;
    width: 40%;
    height: 70%;
    background: #8192A2;
    opacity: 0.35;
    filter: blur(80px);
    z-index: 0;
    pointer-events: none;
  }
  
  ${mediaQueries.medium} {
    padding: 60px 15px 80px;
  }
  
  ${mediaQueries.small} {
    padding: 40px 10px 60px;
  }
`;

const ErrorContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const ErrorCode = styled.h1`
  font-size: 120px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 20px;
  font-weight: bold;
  line-height: 1;
  
  ${mediaQueries.medium} {
    font-size: 80px;
    margin-bottom: 15px;
  }
  
  ${mediaQueries.small} {
    font-size: 60px;
    margin-bottom: 10px;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 48px;
  margin-bottom: 20px;
  font-weight: bold;
  
  ${mediaQueries.medium} {
    font-size: 36px;
    margin-bottom: 15px;
  }
  
  ${mediaQueries.small} {
    font-size: 28px;
    margin-bottom: 10px;
  }
`;

const ErrorSubtitle = styled.p`
  font-size: 24px;
  margin-bottom: 40px;
  line-height: 1.4;
  font-weight: var(--font-weight-medium);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
  
  ${mediaQueries.medium} {
    font-size: 20px;
    margin-bottom: 30px;
    padding: 0 15px;
  }
  
  ${mediaQueries.small} {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 30px;
  
  ${mediaQueries.medium} {
    margin-bottom: 20px;
  }
  
  ${mediaQueries.small} {
    margin-bottom: 15px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
  
  ${mediaQueries.small} {
    gap: 15px;
    flex-direction: column;
    align-items: center;
  }
`;

const Button = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 30px;
  border-radius: 30px;
  text-decoration: none;
  display: inline-block;
  font-weight: var(--font-weight-medium);
  transition: background-color 0.3s;
  border: 2px solid ${({theme}) => theme.colors.secondary};
  
  &:hover {
    background-color: #c42b12;
    border-color: #c42b12;
  }
  
  ${mediaQueries.small} {
    padding: 10px 25px;
    font-size: 14px;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: white;
  border: 2px solid white;
  
  &:hover {
    background-color: white;
    color: #011D3A;
    border-color: white;
  }
`;

const Page404: React.FC = () => {
  return (
    <Page404Container>
      <SEO
        title="Page non trouvée - 404"
        description="La page que vous recherchez n'existe pas ou a été déplacée. Retournez à l'accueil ou explorez nos services."
        canonical={`${window.location.origin}/404`}
      />
      
      <ErrorSection>
        <Header backgroundColor="transparent" />
        
        <ErrorContent>
          <LogoContainer>
            <WeDoDevLogo 
              weDoColor="white" 
              devColor="#DD3114" 
              size="32px" 
            />
          </LogoContainer>
          
          <ErrorCode>404</ErrorCode>
          <ErrorTitle>Page non trouvée</ErrorTitle>
          <ErrorSubtitle>
            Oups ! La page que vous recherchez semble avoir disparu dans le code...
          </ErrorSubtitle>
          
          <ButtonContainer>
            <Button href="/">Retour à l'accueil</Button>
            <SecondaryButton href="/metier">Nos services</SecondaryButton>
          </ButtonContainer>
        </ErrorContent>
      </ErrorSection>
      
      <Footer 
        pageContext="Page 404"
        heading="Une question ?"
        subheading="Notre équipe est là pour vous aider."
        customButton={<ContactButton variant="secondary">Contactez-nous</ContactButton>}
      />
    </Page404Container>
  );
};

export default Page404;